/**
 * De dieren van een klant: tonen, wijzigen en toevoegen (sessie 46, B9).
 *
 * Tot 23-09 stond hier alleen het rekenblok, met de zin "voeg een dier
 * toe op de bestaande klantkaart". Nu kan dat hier: naam, ras,
 * geboortedatum (of leeftijd), gewicht, castratie en allergieën.
 *
 * Geen eigen opslag: dezelfde endpoints als de oude klantkaart
 * (`/api/klanten/{id}/huisdieren`, `/api/huisdieren/{id}`). Allergieën
 * hebben een status. Wat de aanname-engine afleidde heet "aanname"; wat
 * hier wordt opgeslagen, heeft een mens gezien en heet "bevestigd". De
 * allergieregel gaat daarom via de replace-all van `/allergieen`, die
 * precies dat doet.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { huisdierenApi, vertaalApiFout } from "@/lib/api";
import type { DierVoorstel, WerkplekDier } from "@/lib/werkplek-api";
import type {
  AllergieInvoer,
  AllergieRij,
  Diersoort,
  HuisdierAntwoord,
} from "@/types/api";

import "./klantpagina.css";

const SOORTEN: { waarde: Diersoort; label: string }[] = [
  { waarde: "hond", label: "Hond" },
  { waarde: "kat", label: "Kat" },
  { waarde: "knaagdier", label: "Knaagdier" },
  { waarde: "vogel", label: "Vogel" },
  { waarde: "anders", label: "Anders" },
];

/** "3 jaar", "7 maanden", of "leeftijd onbekend". */
export function leeftijdVan(geboortedatum: string | null, nu = new Date()): string {
  if (!geboortedatum) return "leeftijd onbekend";
  const geboren = new Date(geboortedatum);
  if (Number.isNaN(geboren.getTime())) return "leeftijd onbekend";
  const maanden =
    (nu.getFullYear() - geboren.getFullYear()) * 12 +
    (nu.getMonth() - geboren.getMonth()) -
    (nu.getDate() < geboren.getDate() ? 1 : 0);
  if (maanden < 0) return "leeftijd onbekend";
  if (maanden < 24) return `${maanden} ${maanden === 1 ? "maand" : "maanden"}`;
  return `${Math.floor(maanden / 12)} jaar`;
}

function castratieTekst(waarde: boolean | null): string {
  if (waarde === true) return "gecastreerd";
  if (waarde === false) return "niet gecastreerd";
  return "castratie onbekend";
}

/** Allergieën als rijen: uit de nieuwe tabel, anders de oude tekstregel. */
export function allergieRijen(dier: HuisdierAntwoord): AllergieRij[] {
  if (dier.allergie_rijen && dier.allergie_rijen.length > 0) {
    return dier.allergie_rijen;
  }
  return (dier.allergieen ?? "")
    .split(/[,;]/)
    .map((stuk) => stuk.trim())
    .filter(Boolean)
    .map((allergeen, i) => ({
      id: `tekst-${i}`,
      allergeen,
      niveau: "allergie" as const,
      toelichting: null,
      status: "aanname" as const,
      bron: "tekst",
      status_datum: null,
    }));
}

function naarInvoer(tekst: string): AllergieInvoer[] {
  return tekst
    .split(/[,;]/)
    .map((stuk) => stuk.trim())
    .filter(Boolean)
    .map((allergeen) => ({ allergeen, niveau: "allergie" as const }));
}

export function DierenBeheer({
  klantId,
  dieren: werkplekDieren = [],
  voorstel = null,
  meteenToevoegen = false,
}: {
  klantId: string;
  /** De dieren zoals de werkplek ze kent, voor een geschatte leeftijd. */
  dieren?: WerkplekDier[];
  /** Sessie 47 (6a): het dier uit het bericht, om vooraf in te vullen. */
  voorstel?: DierVoorstel | null;
  /** Sessie 47 (6a): geen dier? Dan staat het formulier meteen open. */
  meteenToevoegen?: boolean;
}) {
  const client = useQueryClient();
  const sleutel = ["werkplek", "huisdieren", klantId];
  const dieren = useQuery({
    queryKey: sleutel,
    queryFn: () => huisdierenApi.lijst(klantId),
  });
  const [bewerk, setBewerk] = useState<string | null>(null);
  const [nieuw, setNieuw] = useState(false);
  const [dicht, setDicht] = useState(false);

  function ververs() {
    void client.invalidateQueries({ queryKey: sleutel });
    // De klantpagina en de zaak tonen dezelfde dieren: die mee.
    void client.invalidateQueries({ queryKey: ["werkplek", "klant", klantId] });
    void client.invalidateQueries({ queryKey: ["werkplek", "zaak"] });
  }

  if (dieren.isLoading) return <p className="wp-stil">Dieren worden geladen.</p>;
  if (dieren.isError) {
    return (
      <p className="wp-stil" role="alert">
        {vertaalApiFout(dieren.error, "De dieren van deze klant zijn niet op te halen.")}
      </p>
    );
  }
  const lijst = (dieren.data ?? []).filter((d) => !d.is_overleden);
  const overleden = (dieren.data ?? []).filter((d) => d.is_overleden);
  const formulierOpen = nieuw || (meteenToevoegen && lijst.length === 0 && !dicht);

  return (
    <div className="wp-dieren" data-dieren>
      {lijst.length === 0 ? (
        <p className="wp-stil">
          Nog geen dier bekend.
          {voorstel && voorstel.velden && voorstel.velden.length > 0
            ? " Wat de klant in het bericht noemde staat al ingevuld, als aanname."
            : " Voeg er een toe."}
        </p>
      ) : null}
      {lijst.map((dier) =>
        bewerk === dier.id ? (
          <DierFormulier
            key={dier.id}
            klantId={klantId}
            dier={dier}
            klaar={() => {
              setBewerk(null);
              ververs();
            }}
            annuleer={() => setBewerk(null)}
          />
        ) : (
          <DierRij
            key={dier.id}
            dier={dier}
            geschat={werkplekDieren.find((d) => d.id === dier.id)?.leeftijd_maanden ?? null}
            onWijzig={() => setBewerk(dier.id)}
            onBevestigd={ververs}
          />
        ),
      )}
      {overleden.length > 0 ? (
        <p className="wp-stil">
          Overleden: {overleden.map((d) => d.naam).join(", ")}
        </p>
      ) : null}

      {formulierOpen ? (
        <DierFormulier
          klantId={klantId}
          dier={null}
          voorstel={lijst.length === 0 ? voorstel : null}
          klaar={() => {
            setNieuw(false);
            setDicht(true);
            ververs();
          }}
          annuleer={() => {
            setNieuw(false);
            setDicht(true);
          }}
        />
      ) : (
        <div className="wp-knoprij">
          <button
            type="button"
            className="wp-knop"
            data-dier-toevoegen
            onClick={() => setNieuw(true)}
          >
            Dier toevoegen
          </button>
        </div>
      )}
    </div>
  );
}

function DierRij({
  dier,
  geschat,
  onWijzig,
  onBevestigd,
}: {
  dier: HuisdierAntwoord;
  /** Leeftijd in maanden uit de aanname-engine, als er geen datum is. */
  geschat: number | null;
  onWijzig: () => void;
  onBevestigd: () => void;
}) {
  const rijen = allergieRijen(dier);
  const aannames = rijen.filter((r) => r.status !== "bevestigd");
  const bevestig = useMutation({
    mutationFn: () =>
      huisdierenApi.zetAllergieen(
        dier.id,
        rijen.map((r) => ({
          allergeen: r.allergeen,
          niveau: r.niveau,
          toelichting: r.toelichting,
        })),
      ),
    onSuccess: onBevestigd,
  });
  const soort = SOORTEN.find((s) => s.waarde === dier.diersoort)?.label ?? dier.diersoort;
  return (
    <div className="wp-dier" data-dier={dier.id}>
      <div className="wp-dier-kop">
        <b>{dier.naam}</b>
        <span>
          {soort}
          {dier.ras ? ` · ${dier.ras}` : " · ras onbekend"} ·{" "}
          {dier.geboortedatum || geschat === null
            ? leeftijdVan(dier.geboortedatum)
            : `${geschat < 24 ? `${geschat} maanden` : `${Math.floor(geschat / 12)} jaar`} (aanname)`}
          {dier.geboortedatum_geschat && dier.geboortedatum ? " (geschat)" : ""} ·{" "}
          {dier.gewicht_kg ? `${Number(dier.gewicht_kg).toLocaleString("nl-NL")} kg` : "gewicht onbekend"} ·{" "}
          {castratieTekst(dier.castratie)}
        </span>
        <button type="button" className="wp-knop wp-knop-klein" data-dier-wijzig onClick={onWijzig}>
          Wijzig
        </button>
      </div>
      <div className="wp-dier-allergie">
        <span className="wp-stil">Allergieën:</span>{" "}
        {rijen.length === 0 ? (
          <span className="wp-stil">geen bekend</span>
        ) : (
          rijen.map((r) => (
            <span
              key={r.id}
              className={`wp-chip ${r.status === "bevestigd" ? "wp-chip-groen" : "wp-chip-grijs"}`}
              data-allergie-status={r.status ?? "aanname"}
              title={r.status === "bevestigd" ? "Bevestigd" : "Aanname, nog niet bevestigd"}
            >
              {r.allergeen} · {r.status === "bevestigd" ? "bevestigd" : "aanname"}
            </span>
          ))
        )}
        {aannames.length > 0 ? (
          <button
            type="button"
            className="wp-knop wp-knop-klein"
            data-allergie-bevestig
            disabled={bevestig.isPending}
            onClick={() => bevestig.mutate()}
          >
            {bevestig.isPending ? "Bezig…" : "Bevestig"}
          </button>
        ) : null}
        {bevestig.isError ? (
          <span className="wp-stil" role="alert">
            {vertaalApiFout(bevestig.error, "Bevestigen lukte niet.")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function tekstVan(waarde: number | null | undefined): string {
  return waarde == null ? "" : String(waarde).replace(".", ",");
}

function DierFormulier({
  klantId,
  dier,
  voorstel = null,
  klaar,
  annuleer,
}: {
  klantId: string;
  dier: HuisdierAntwoord | null;
  /** Sessie 47 (6a): vooraf ingevuld uit het bericht, status aanname. */
  voorstel?: DierVoorstel | null;
  klaar: () => void;
  annuleer: () => void;
}) {
  const [naam, setNaam] = useState(dier?.naam ?? voorstel?.naam ?? "");
  const [soort, setSoort] = useState<Diersoort>(
    dier?.diersoort ?? (voorstel?.diersoort as Diersoort | null) ?? "hond",
  );
  const [ras, setRas] = useState(dier?.ras ?? voorstel?.ras ?? "");
  const [geboortedatum, setGeboortedatum] = useState(dier?.geboortedatum ?? "");
  const [leeftijd, setLeeftijd] = useState("");
  // Sessie 47 (6a/6b): een pup of kitten telt in maanden.
  const [maanden, setMaanden] = useState(tekstVan(voorstel?.leeftijd_maanden));
  const [volwassen, setVolwassen] = useState(
    dier?.verwacht_volwassen_gewicht_kg != null
      ? String(dier.verwacht_volwassen_gewicht_kg)
      : tekstVan(voorstel?.verwacht_volwassen_gewicht_kg),
  );
  const [gewicht, setGewicht] = useState(
    dier?.gewicht_kg ?? tekstVan(voorstel?.gewicht_kg),
  );
  const [castratie, setCastratie] = useState(
    dier?.castratie === true ? "ja" : dier?.castratie === false ? "nee" : "",
  );
  const beginAllergie = dier ? allergieRijen(dier).map((r) => r.allergeen).join(", ") : "";
  const [allergie, setAllergie] = useState(beginAllergie);

  const opslaan = useMutation({
    mutationFn: async () => {
      const velden = {
        naam: naam.trim(),
        ras: ras.trim() || undefined,
        geboortedatum: geboortedatum || undefined,
        leeftijd_jaren:
          !geboortedatum && !maanden.trim() && leeftijd.trim()
            ? Number(leeftijd.replace(",", "."))
            : undefined,
        leeftijd_maanden:
          !geboortedatum && maanden.trim()
            ? Math.round(Number(maanden.replace(",", ".")))
            : undefined,
        gewicht_kg: String(gewicht).trim() ? String(gewicht).replace(",", ".") : undefined,
        verwacht_volwassen_gewicht_kg: volwassen.trim()
          ? volwassen.replace(",", ".")
          : undefined,
        castratie: castratie === "" ? undefined : castratie === "ja",
      };
      // Sessie 47 (6a): wat uit het bericht kwam en niet is gewijzigd,
      // blijft een aanname; wat JP wijzigde of zelf invulde is bevestigd.
      const aanname: string[] = [];
      if (voorstel?.velden) {
        const zelfde: Record<string, boolean> = {
          naam: naam.trim() === (voorstel.naam ?? ""),
          diersoort: soort === voorstel.diersoort,
          ras: ras.trim() === (voorstel.ras ?? ""),
          leeftijd_maanden: maanden === tekstVan(voorstel.leeftijd_maanden),
          gewicht_kg: String(gewicht) === tekstVan(voorstel.gewicht_kg),
          verwacht_volwassen_gewicht_kg:
            volwassen === tekstVan(voorstel.verwacht_volwassen_gewicht_kg),
        };
        for (const veld of voorstel.velden) if (zelfde[veld]) aanname.push(veld);
      }
      const opgeslagen = dier
        ? await huisdierenApi.wijzig(dier.id, velden)
        : await huisdierenApi.maak(klantId, {
            ...velden,
            diersoort: soort,
            aanname_velden: aanname.length > 0 ? aanname : undefined,
          });
      if (allergie.trim() !== beginAllergie.trim()) {
        await huisdierenApi.zetAllergieen(opgeslagen.id, naarInvoer(allergie));
      }
      return opgeslagen;
    },
    onSuccess: klaar,
  });

  const redenUit = naam.trim() === "" ? "Een dier heeft een naam nodig." : null;

  return (
    <form
      className="wp-dier wp-dier-formulier"
      data-dier-formulier={dier?.id ?? "nieuw"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!redenUit) opslaan.mutate();
      }}
    >
      <div className="wp-calc">
        <div>
          <label htmlFor={`dier-naam-${dier?.id ?? "nieuw"}`}>Naam</label>
          <input
            id={`dier-naam-${dier?.id ?? "nieuw"}`}
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
          />
        </div>
        {dier ? null : (
          <div>
            <label htmlFor="dier-soort-nieuw">Soort</label>
            <select
              id="dier-soort-nieuw"
              value={soort}
              onChange={(e) => setSoort(e.target.value as Diersoort)}
            >
              {SOORTEN.map((s) => (
                <option key={s.waarde} value={s.waarde}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor={`dier-ras-${dier?.id ?? "nieuw"}`}>Ras</label>
          <input
            id={`dier-ras-${dier?.id ?? "nieuw"}`}
            value={ras}
            placeholder="onbekend"
            onChange={(e) => setRas(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor={`dier-geboren-${dier?.id ?? "nieuw"}`}>Geboortedatum</label>
          <input
            id={`dier-geboren-${dier?.id ?? "nieuw"}`}
            type="date"
            value={geboortedatum}
            onChange={(e) => setGeboortedatum(e.target.value)}
          />
        </div>
        {geboortedatum ? null : (
          <div>
            <label htmlFor={`dier-maanden-${dier?.id ?? "nieuw"}`}>
              Of leeftijd (maanden)
              {voorstel?.velden?.includes("leeftijd_maanden") ? " · aanname" : ""}
            </label>
            <input
              id={`dier-maanden-${dier?.id ?? "nieuw"}`}
              inputMode="numeric"
              value={maanden}
              placeholder="pup of kitten"
              onChange={(e) => setMaanden(e.target.value)}
            />
          </div>
        )}
        {geboortedatum || maanden.trim() ? null : (
          <div>
            <label htmlFor={`dier-leeftijd-${dier?.id ?? "nieuw"}`}>Of leeftijd (jaar)</label>
            <input
              id={`dier-leeftijd-${dier?.id ?? "nieuw"}`}
              inputMode="decimal"
              value={leeftijd}
              placeholder="bijv. 3"
              onChange={(e) => setLeeftijd(e.target.value)}
            />
          </div>
        )}
        <div>
          <label htmlFor={`dier-gewicht-${dier?.id ?? "nieuw"}`}>Gewicht (kg)</label>
          <input
            id={`dier-gewicht-${dier?.id ?? "nieuw"}`}
            inputMode="decimal"
            value={gewicht}
            placeholder="onbekend"
            onChange={(e) => setGewicht(e.target.value)}
          />
        </div>
        {soort === "hond" ? (
          <div>
            <label htmlFor={`dier-volwassen-${dier?.id ?? "nieuw"}`}>
              Verwacht volwassen gewicht (kg)
              {voorstel?.velden?.includes("verwacht_volwassen_gewicht_kg") ? " · aanname" : ""}
            </label>
            <input
              id={`dier-volwassen-${dier?.id ?? "nieuw"}`}
              inputMode="decimal"
              value={volwassen}
              placeholder="nodig voor een pup"
              onChange={(e) => setVolwassen(e.target.value)}
            />
          </div>
        ) : null}
        <div>
          <label htmlFor={`dier-castratie-${dier?.id ?? "nieuw"}`}>Castratie</label>
          <select
            id={`dier-castratie-${dier?.id ?? "nieuw"}`}
            value={castratie}
            onChange={(e) => setCastratie(e.target.value)}
          >
            <option value="">Onbekend</option>
            <option value="ja">Gecastreerd</option>
            <option value="nee">Niet gecastreerd</option>
          </select>
        </div>
        <div className="wp-calc-breed">
          <label htmlFor={`dier-allergie-${dier?.id ?? "nieuw"}`}>
            Allergieën (komma ertussen; opslaan = bevestigd)
          </label>
          <input
            id={`dier-allergie-${dier?.id ?? "nieuw"}`}
            value={allergie}
            placeholder="geen bekend"
            onChange={(e) => setAllergie(e.target.value)}
          />
        </div>
      </div>
      <div className="wp-knoprij">
        <button
          type="submit"
          className="wp-knop wp-knop-primair"
          data-dier-opslaan
          disabled={redenUit !== null || opslaan.isPending}
        >
          {opslaan.isPending ? "Bezig…" : dier ? "Opslaan" : "Toevoegen"}
        </button>
        <button type="button" className="wp-knop" onClick={annuleer}>
          Annuleren
        </button>
        {redenUit ? <span className="wp-stil">{redenUit}</span> : null}
        {opslaan.isError ? (
          <span className="wp-stil" role="alert">
            {vertaalApiFout(opslaan.error, "Opslaan lukte niet.")}
          </span>
        ) : null}
      </div>
    </form>
  );
}
